import React, { useEffect, useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, Loader2, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import PrivacyNotice from '../components/PrivacyNotice';
import { useToast } from '../hooks/use-toast';
import { siteInfo } from '../mockData';
import { contactInfo as contactConfig } from '../config/siteConfig';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^(?:\+33|0033|0)[1-9]\d{8}$/;

const validateForm = (data) => {
  const errors = {};

  if (!data.name.trim()) {
    errors.name = "Le nom est requis.";
  }

  if (!data.email.trim()) {
    errors.email = "L'email est requis.";
  } else if (!EMAIL_REGEX.test(data.email.trim())) {
    errors.email = "Adresse email invalide.";
  }

  if (!data.phone.trim()) {
    errors.phone = "Le téléphone est requis.";
  } else if (!PHONE_REGEX.test(data.phone.replace(/[\s.-]/g, ''))) {
    errors.phone = "Numéro de téléphone invalide.";
  }

  if (!data.message.trim()) {
    errors.message = "Le message est requis.";
  }

  return errors;
};

const Contact = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    website: '' // honeypot anti-spam, doit rester vide
  });
  const [status, setStatus] = useState('idle'); // 'idle' | 'loading' | 'success' | 'error'
  const [errors, setErrors] = useState({});
  const { toast } = useToast();

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    setStatus('loading');

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nom: formData.name,
          email: formData.email,
          telephone: formData.phone,
          message: formData.message,
          website: formData.website
        })
      });
      const data = await res.json();

      if (!res.ok || !data.ok) {
        throw new Error(data.error || "Une erreur est survenue.");
      }

      setStatus('success');
      toast({
        title: "Message envoyé !",
        description: "Je vous recontacterai dans les plus brefs délais.",
      });
      setFormData({ name: '', email: '', phone: '', message: '', website: '' });
    } catch (err) {
      setStatus('error');
      toast({
        title: "Erreur lors de l'envoi",
        description: "Votre message n'a pas pu être envoyé. Merci de réessayer.",
        variant: "destructive",
      });
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: undefined });
    }
  };

  const contactCards = [
    {
      icon: MapPin,
      title: "Adresse",
      content: siteInfo.address
    },
    {
      icon: Phone,
      title: "Téléphone",
      content: siteInfo.phone,
      href: contactConfig.phoneHref
    },
    {
      icon: MessageCircle,
      title: "WhatsApp",
      content: contactConfig.whatsapp,
      href: contactConfig.whatsappHref,
      external: true
    },
    {
      icon: Mail,
      title: "Email",
      content: siteInfo.email,
      href: `mailto:${siteInfo.email}`
    },
    {
      icon: Clock,
      title: "Horaires",
      content: siteInfo.hours
    }
  ];

  return (
    <div className="min-h-screen bg-[#F5F3EB]">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-[#F5F3EB] -z-10"></div>

        <div className="absolute top-20 right-10 w-72 h-72 bg-[#EAA93D]/10 rounded-full blur-3xl -z-10"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#21167D]/5 rounded-full blur-3xl -z-10"></div>

        <div className="max-w-7xl mx-auto text-center">
          <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h1 className="font-display text-5xl md:text-6xl font-semibold text-[#21167D] mb-6">
              Contactez-moi
            </h1>
            <p className="font-accent italic text-xl text-[#4A4458] max-w-3xl mx-auto leading-relaxed">
              Je suis à votre écoute pour répondre à toutes vos questions
            </p>
          </div>
        </div>
      </section>

      {/* Decorative skyline banner */}
      <section className="px-4 pb-4">
        <div className="max-w-7xl mx-auto rounded-2xl overflow-hidden shadow-md">
          <img
            src="/bandeau-skyline.png"
            alt=""
            className="w-full h-24 md:h-36 object-cover"
          />
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 px-4 bg-[#F5F3EB]">
        <div className="max-w-7xl mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {contactCards.map((info, index) => (
              <Card
                key={index}
                className={`group hover:shadow-xl transition-all duration-500 hover:-translate-y-2 bg-[#FDFBF5] ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-[#21167D] rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform shadow-lg">
                    <info.icon className="w-8 h-8 text-[#F5F3EB]" />
                  </div>
                  <h3 className="font-accent font-semibold text-lg text-[#21167D] mb-2">{info.title}</h3>
                  {info.href ? (
                    <a
                      href={info.href}
                      target={info.external ? "_blank" : undefined}
                      rel={info.external ? "noopener noreferrer" : undefined}
                      className="text-[#4A4458] text-sm hover:text-[#21167D] transition-colors break-words"
                    >
                      {info.content}
                    </a>
                  ) : (
                    <p className="text-[#4A4458] text-sm">{info.content}</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map Section */}
      <section className="py-20 px-4 bg-[#DCB071]/10">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="space-y-8">
              <div>
                <h2 className="font-display text-4xl font-semibold text-[#21167D] mb-4">
                  Envoyez-moi un message
                </h2>
                <p className="text-lg text-[#4A4458]">
                  Remplissez le formulaire ci-dessous et je vous répondrai dans les meilleurs délais.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-[#21167D] mb-2">
                    Nom complet *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Votre nom"
                    className="w-full bg-[#FDFBF5]"
                  />
                  {errors.name && (
                    <p className="text-[0.8rem] font-medium text-destructive mt-1">{errors.name}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-[#21167D] mb-2">
                    Email *
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="votre.email@exemple.com"
                    className="w-full bg-[#FDFBF5]"
                  />
                  {errors.email && (
                    <p className="text-[0.8rem] font-medium text-destructive mt-1">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-[#21167D] mb-2">
                    Téléphone *
                  </label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="06 XX XX XX XX"
                    className="w-full bg-[#FDFBF5]"
                  />
                  {errors.phone && (
                    <p className="text-[0.8rem] font-medium text-destructive mt-1">{errors.phone}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-[#21167D] mb-2">
                    Message *
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    aria-describedby="message-help"
                    placeholder="Votre message..."
                    rows={6}
                    className="w-full bg-[#FDFBF5] resize-none"
                  />
                  <p id="message-help" className="text-xs text-[#4A4458] mt-1">
                    Merci de ne pas indiquer d'information médicale dans ce message.
                  </p>
                  {errors.message && (
                    <p className="text-[0.8rem] font-medium text-destructive mt-1">{errors.message}</p>
                  )}
                </div>

                <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
                  <label htmlFor="website">Site web</label>
                  <input
                    id="website"
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formData.website}
                    onChange={handleChange}
                  />
                </div>

                <PrivacyNotice />

                <Button
                  type="submit"
                  size="lg"
                  className="w-full shadow-lg"
                  disabled={status === 'loading'}
                >
                  {status === 'loading' ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Envoyer le message
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Map / Additional Info */}
            <div className="space-y-8">
              <Card className="overflow-hidden">
                <div className="h-96 bg-[#FDFBF5] flex items-center justify-center">
                  {/* Google Maps Embed would go here */}
                  <div className="text-center p-8">
                    <MapPin className="w-16 h-16 text-[#21167D] mx-auto mb-4" />
                    <h3 className="font-accent font-semibold text-2xl text-[#21167D] mb-2">
                      Cabinet à Marseille 1er
                    </h3>
                    <p className="text-[#4A4458] mb-4">{siteInfo.address}</p>
                    <p className="text-sm text-[#4A4458]/70">
                      Facilement accessible en transports en commun
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="bg-[#21167D] text-[#F5F3EB] border-0 shadow-lg">
                <CardContent className="p-8">
                  <h3 className="font-accent font-semibold text-2xl mb-4">
                    Première consultation
                  </h3>
                  <p className="mb-6 leading-relaxed text-[#F5F3EB]/90">
                    La première séance est l'occasion de faire connaissance, d'échanger sur vos besoins et de définir ensemble les modalités de l'accompagnement.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-[#EAA93D] rounded-full"></div>
                      <span>Espace confidentiel et bienveillant</span>
                    </li>
                    <li className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-[#EAA93D] rounded-full"></div>
                      <span>Sans engagement</span>
                    </li>
                    <li className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-[#EAA93D] rounded-full"></div>
                      <span>Tarif identique aux séances suivantes</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
